import React, { FunctionComponent, SVGProps } from 'react';

import styled from '@emotion/styled';
import { useMediaQuery } from '@react-hook/media-query';
import { Button, ButtonProps } from 'src/shared/ds/components/Button';
import { SkeletonPlaceholder } from 'src/shared/ds/components/SkeletonPlaceholder';
import {
    BodyTextMedium,
    BodyTextSmall,
    ComponentTextMedium,
    ComponentTextSmall,
    HeadingText30,
    HeadingText40,
} from 'src/shared/ds/Typography';
import { dsColors, layout, mediaQuery } from 'src/shared/ds/variables';
import { pxToRem } from 'src/shared/helpers/pxToRem';

interface Props {
    title: string;
    description: string;
    image?: string | FunctionComponent<SVGProps<SVGSVGElement>>;
    backgroundColor?: string;
    subtitle?: string;
    buttonTitle?: string;
    onClick?: () => void;
    buttonProps?: Partial<ButtonProps>;
}

export enum ContentBannerTestID {
    Container = 'ContentBannerTestID.Container',
}

export const ContentBanner: React.FC<Props> = ({
    title,
    backgroundColor,
    description,
    image,
    subtitle,
    buttonTitle,
    onClick,
    buttonProps = {},
}) => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('md', true));
    const colorfulVariant = !!backgroundColor;

    const TitleTextComponent = smallScreenDetected ? HeadingText30 : HeadingText40;
    const SubtitleTextComponent = smallScreenDetected ? ComponentTextSmall : ComponentTextMedium;
    const DescriptionTextComponent = smallScreenDetected ? BodyTextSmall : BodyTextMedium;

    const displayImage = () => {
        if (!image) {
            return;
        }

        if (typeof image === 'string') {
            // we support passing both url to image and inline svg component
            // to style them identically we have to use className
            return <img src={image} className="image" />;
        }

        const Image = image;

        return <Image className={'image'} />;
    };
    return (
        <Container
            outlined={!colorfulVariant}
            css={[backgroundColor && { backgroundColor }]}
            data-testid={ContentBannerTestID.Container}
        >
            <TextContainer>
                {subtitle && (
                    <SubtitleTextComponent
                        fontColor={colorfulVariant ? 'white' : 'darkGrey'}
                        marginBottom={4}
                    >
                        {subtitle}
                    </SubtitleTextComponent>
                )}
                <TitleTextComponent fontColor={colorfulVariant ? 'white' : 'primaryDark'}>
                    {title}
                </TitleTextComponent>
                <DescriptionTextComponent
                    fontColor={colorfulVariant ? 'white' : 'darkGrey'}
                    marginTop={4}
                >
                    {description}
                </DescriptionTextComponent>
                {onClick && buttonTitle && (
                    <Button
                        title={buttonTitle}
                        marginTop={12}
                        rounded
                        variant={colorfulVariant ? 'primary' : 'secondary'}
                        onClick={onClick}
                        size={smallScreenDetected ? 'small' : 'medium'}
                        {...buttonProps}
                    />
                )}
            </TextContainer>

            <ImageContainer>{displayImage()}</ImageContainer>
        </Container>
    );
};

export const ContentBannerSkeleton: React.FC = () => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('md', true));
    return (
        <SkeletonPlaceholder
            width="100%"
            height={smallScreenDetected ? 184 : '100%'}
            borderRadius={6}
        />
    );
};

const TextContainer = styled.div({
    width: '57%',
});

const Container = styled.div<{ outlined: boolean }>(
    {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        paddingLeft: layout.containerPaddingX,
        minHeight: pxToRem(184),
        justifyContent: 'center',
        borderRadius: pxToRem(6),
        paddingTop: pxToRem(28),
        paddingBottom: pxToRem(28),
        position: 'relative',
    },
    ({ outlined }) =>
        outlined
            ? {
                  borderStyle: 'solid',
                  borderWidth: pxToRem(2),
                  borderRadius: pxToRem(8),
                  borderColor: dsColors.lightGrey,
              }
            : {},
);

const ImageContainer = styled.div({
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '40%',
    maxWidth: pxToRem(250),
    maxHeight: '100%',
    overflow: 'hidden',

    [mediaQuery.maxWidth('md')]: {
        maxWidth: pxToRem(150),
    },

    '.image': {
        width: '100%',
    },
});
